/**
 * Brand-only mutations. Brand is the gatekeeper who approves scripts and
 * content submitted by creators/admin. BE enforces brand role in handler.
 */
import { apiClient } from '@discovr/common';
import type { CampaignEditRequest, Product, ProductInput, SubmitEditRequestPayload } from '@discovr/types';

/**
 * PUT /campaigns/:id/brief — replace the campaign's products[] catalog
 * (Multi-Product Campaigns). New products omit product_id (server mints one);
 * existing ids are preserved. Full replace. Returns the saved products (with ids).
 */
const updateCampaignBrief = (id: string, products: ProductInput[]): Promise<Product[]> =>
  apiClient
    .put<{ data: { products: Product[] } }>(`/campaigns/${encodeURIComponent(id)}/brief`, {
      products,
    })
    .then((res) => res.data.data.products);

/**
 * POST /campaigns/:id/edit-requests — submit a batch of proposed campaign
 * field changes for admin review. Only include keys for fields actually
 * touched (see SubmitEditRequestPayload). Merges into the campaign's open
 * request if one exists (BE-side); returns the resulting request.
 */
const submitEditRequest = (id: string, payload: SubmitEditRequestPayload): Promise<CampaignEditRequest> =>
  apiClient
    .post<{ data: CampaignEditRequest }>(`/campaigns/${encodeURIComponent(id)}/edit-requests`, payload)
    .then((res) => res.data.data);

const approveScript = (rosterEntryId: string): Promise<void> =>
  apiClient
    .post(`/roster/${encodeURIComponent(rosterEntryId)}/approve-script`, {})
    .then(() => undefined);

const approveContent = (rosterEntryIds: string[]): Promise<void[]> =>
  Promise.all(
    rosterEntryIds.map((id) =>
      apiClient
        .post(`/roster/${encodeURIComponent(id)}/approve-content`, {})
        .then(() => undefined),
    ),
  );

export function createBrandMutationsApi() {
  return {
    updateCampaignBrief,
    submitEditRequest,
    approveScript,
    approveContent,
  };
}

export type BrandMutationsApi = ReturnType<typeof createBrandMutationsApi>;
